import { useEffect, useMemo,useRef, useState } from "react";

import { t } from "i18next";

import WPTertiaryButton from "../../packages/WPTertiaryButton";

import FileMetaCard from "../FileMetaCard";

import { ChevronDown, ChevronUp } from "../../assets/icons";

import { combineClassNames } from "../../utils/appUtils";

import "./DataAppendList.scss";

const DataAppendList = ({ datasets = [], onViewData, onDelete, className }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const scrollContainerRef = useRef(null);

  const getDisplayedItems = () => {
    // <=3 datasets or Expanded state: show all items
    if (datasets.length <= 3 || isExpanded) {
      return {
        visibleItems: datasets.slice(0, -1),
        hiddenCount: 0,
        hiddenDatasets: [],
        lastDataset: datasets[datasets.length - 1],
      };
    }

    // Collapsed state: first 2 + last 1
    const first2 = datasets.slice(0, 2);
    const last1 = datasets[datasets.length - 1];
    const hidden = datasets.slice(2, -1);
    const hiddenCount = hidden.length;

    return {
      visibleItems: first2,
      hiddenCount,
      hiddenDatasets: hidden,
      lastDataset: last1,
    };
  };

  const { visibleItems, hiddenCount, hiddenDatasets, lastDataset } = useMemo(
    () => getDisplayedItems(),
    [datasets, isExpanded]
  );

  const handleExpandClick = () => {
    setIsExpanded(true);
  };

  const handleShowLess = () => {
    setIsExpanded(false);
  };

  const renderDatasetItem = (dataset, isLastItem = false) => {
    const {
      append_id,
      file_name,
      start_date,
      end_date,
      user_id,
      appended_at,
      no_of_records,
    } = dataset;

    return (
      <div
        key={append_id}
        className={combineClassNames(
          "data-append-timeline-item",
          isLastItem && "last-item"
        )}
      >
        <div className="timeline-dot" />

        <div className="timeline-item-content">
          <FileMetaCard
            key={append_id}
            fileName={file_name}
            startDate={start_date}
            endDate={end_date}
            recordCount={no_of_records}
            uploadedBy={user_id}
            appendedAt={appended_at}
            onViewData={() => onViewData(append_id)}
            {...(isLastItem ? { onDelete: () => onDelete(append_id) } : {})}
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isExpanded && scrollContainerRef.current) {
      setTimeout(() => {
        scrollContainerRef.current.scrollTop =
          scrollContainerRef.current.scrollHeight;
      }, 0);
    }
  }, [isExpanded]);

  // Reset to collapsed state if datasets change to <=3
  useEffect(() => {
    if (datasets.length <= 3) {
      setIsExpanded(false);
    }
  }, [datasets]);

  return (
    <div className={combineClassNames("data-append-list-wrapper", className)}>
      <div
        className={combineClassNames(
          "data-append-timeline-container",
          isExpanded && "expanded"
        )}
        ref={scrollContainerRef}
      >
        {datasets.length > 0 ? (
          <>
            {/* Render visible items (first 2 in collapsed state) */}
            {visibleItems.map((dataset) => renderDatasetItem(dataset))}

            {/* Render hidden items when expanded - these appear BEFORE the button */}
            {isExpanded &&
              hiddenDatasets.map((dataset) => renderDatasetItem(dataset))}

            {/* Button Container - shown BEFORE last item */}
            {datasets.length > 3 && (
              <div className="expansion-button-container">
                {!isExpanded ? (
                  <WPTertiaryButton
                    variant="contained"
                    type="secondary"
                    size="small"
                    onClick={handleExpandClick}
                    label={`+${hiddenCount} ${t(
                      "dataAppend.moreAppendedDataSets"
                    )}`}
                    icon={
                      <ChevronDown
                        aria-label="Chevron Down Icon"
                        width={10}
                        height={10}
                      />
                    }
                    iconPlacement="before"
                    className="expansion-toggle-btn"
                  />
                ) : (
                  <WPTertiaryButton
                    variant="outlined"
                    type="secondary"
                    onClick={handleShowLess}
                    label={t("dataAppend.showLess")}
                    size="small"
                    icon={
                      <ChevronUp
                        aria-label="Chevron Up Icon"
                        width={10}
                        height={10}
                      />
                    }
                    iconPlacement="before"
                    className="expansion-toggle-btn show-less-btn"
                  />
                )}
              </div>
            )}

            {/* Render last item - always at the end */}
            {lastDataset && renderDatasetItem(lastDataset, true)}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default DataAppendList;
